import { useRouter } from 'next/router';
import { Trans, useTranslation } from 'next-i18next';
import type { ReactNode } from 'react';

import { AVATAR_LG, CONTAINER, EM_ACCENT, EYEBROW, EYEBROW_MARK } from '../classes';
import { CountFigure, useCountProgress } from '../count-up';
import { DigitalRain } from '../digital-rain';
import { IMG, m } from '../shared';

export const HERO_AVATARS = [
  { slug: 'coldzera', name: 'coldzera' },
  { slug: 'sacy', name: 'Sacy' },
  { slug: 'suja', name: 'Suja' },
];

const PROOF_ITEM = "relative grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3 overflow-hidden rounded-2xl border border-[#2C3B31] bg-[linear-gradient(180deg,rgb(16_23_17/.88),rgb(10_15_11/.9))] px-5 py-4 shadow-[0_8px_24px_rgb(0_0_0/.45),0_2px_6px_rgb(0_0_0/.3),inset_0_1px_0_rgb(255_255_255/.04)] before:absolute before:inset-y-0 before:start-0 before:w-0.5 before:bg-[image:var(--rail)] before:content-[''] forced-colors:border-[CanvasText] md:max-[1088px]:grid-cols-[minmax(0,1fr)] md:max-[1088px]:grid-rows-[auto_1fr_auto] md:max-[1088px]:content-stretch md:max-[1088px]:items-start md:max-[1088px]:gap-3";
const PROOF_ICON = 'grid size-11 flex-none place-items-center rounded-xl border border-[#93FF18]/10 bg-[#93FF18]/[.07]';
const PROOF_TEXT = 'min-w-0 break-words text-[1rem] leading-[1.35] text-[#CBD5E1] [&_b]:font-semibold [&_b]:text-white';
const PROOF_FIGURE = 'text-[1.35em] font-bold tabular-nums tracking-[-.02em] text-[#93FF18] forced-colors:text-[LinkText]';
const PROOF_BAR = 'col-span-full h-[3px] overflow-hidden rounded-[2px] bg-white/[.07] forced-colors:border forced-colors:border-[CanvasText] md:max-[1088px]:mt-2 md:max-[1088px]:self-end';
const PROOF_FILL = 'block h-full origin-left bg-[linear-gradient(90deg,rgb(61_255_122/.55),#93FF18)] shadow-[0_0_10px_rgb(147_255_24/.5)] forced-colors:bg-[Highlight] rtl:origin-right';

function ProofIcon({ name }: { name: 'ping' | 'fps' | 'input' }) {
  return (
    <span className={PROOF_ICON} aria-hidden='true'>
      <picture>
        <source type='image/avif' srcSet={`${IMG}/ic-${name}-96.avif`} />
        <img className='size-6 object-contain' src={`${IMG}/ic-${name}-96.webp`} alt='' width={96} height={96} decoding='async' />
      </picture>
    </span>
  );
}

function ProofBar({ width, progress }: { width: string; progress: number }) {
  return (
    <span className={PROOF_BAR} aria-hidden='true'>
      <i className={`${PROOF_FILL} ${width}`} style={progress >= 1 ? undefined : { transform: `scaleX(${progress.toFixed(4)})` }} />
    </span>
  );
}

export function Avatar({ slug, name, size, className }: { slug: string; name: string; size: 28 | 40; className: string }) {
  return (
    // The tooltip (`::after` + data-name) opens on hover and on keyboard focus, as in the design.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
    <li className={className} tabIndex={0} data-name={name}>
      <picture>
        <source type='image/avif' srcSet={`${IMG}/avatar-${slug}-64.avif 64w, ${IMG}/avatar-${slug}-128.avif 128w`} sizes={`${size}px`} />
        <img className='size-full rounded-full object-cover' src={`${IMG}/avatar-${slug}-64.webp`} srcSet={`${IMG}/avatar-${slug}-64.webp 64w, ${IMG}/avatar-${slug}-128.webp 128w`} sizes={`${size}px`} alt={name} width={128} height={128} loading='lazy' decoding='async' />
      </picture>
    </li>
  );
}

export function Hero({ formSlot }: { formSlot: ReactNode }) {
  const { t } = useTranslation('pages/trial-matrix');
  const { locale } = useRouter();
  const [proofRef, progress] = useCountProgress<HTMLUListElement>(1500);
  const figure = <CountFigure progress={progress} locale={locale} className={PROOF_FIGURE} valueClassName='justify-self-start' />;

  return (
    <section className='relative isolate scroll-mt-6 border-b border-[#93FF18]/10 pb-[clamp(3rem,2rem+4vw,6rem)] pt-[clamp(2.5rem,1.5rem+4vw,5rem)]' id='topo'>
      <DigitalRain variant='hero' />
      <div className={m('hero__scrim')} aria-hidden='true' />
      <div className='pointer-events-none absolute inset-0 z-[1] bg-[radial-gradient(46rem_30rem_at_78%_22%,rgb(147_255_24/.13),transparent_68%),radial-gradient(38rem_26rem_at_8%_6%,rgb(61_255_122/.10),transparent_70%)] forced-colors:hidden' aria-hidden='true' />
      <div className='pointer-events-none absolute inset-x-0 bottom-0 z-[1] h-[34%] bg-[linear-gradient(180deg,transparent,rgb(7_10_8/.92)_62%,#070A08)] forced-colors:hidden' aria-hidden='true' />

      <div className={`${CONTAINER} relative z-[2] grid gap-[clamp(2.25rem,1rem+3.6vw,3.5rem)] min-[1088px]:grid-cols-[minmax(0,1fr)_minmax(0,min(34.8rem,48%))] min-[1088px]:items-start`}>
        <div>
          <p className={EYEBROW}>
            <span className={EYEBROW_MARK} aria-hidden='true'>
              &gt;
            </span>{' '}
            {t('lp.hero.eyebrow')}
          </p>

          <h1 className={`max-w-[15em] text-balance text-[min(clamp(2.25rem,1.45rem+3.1vw,4.5rem),11vw)] font-bold leading-[1.02] tracking-[-.038em] text-white ${EM_ACCENT} [&_em]:[text-shadow:0_0_30px_rgb(147_255_24/.45)] min-[384px]:[&_em]:whitespace-nowrap`}>
            <Trans t={t} i18nKey='lp.hero.title' components={{ em: <em /> }} />
          </h1>

          <p className='mt-5 max-w-[34em] text-[length:var(--fs-lg)] leading-[1.6] text-[#CBD5E1]'>{t('lp.hero.lead')}</p>

          <ul ref={proofRef} className='mt-[clamp(1.75rem,1rem+2vw,2.5rem)] grid gap-3 md:max-[1088px]:grid-cols-3 min-[1088px]:max-w-[34rem]'>
            <li className={PROOF_ITEM}>
              <ProofIcon name='ping' />
              <span className={PROOF_TEXT}>
                <Trans t={t} i18nKey='lp.hero.proofPing' components={{ b: <b />, n: figure }} />
              </span>
              <ProofBar width='w-[80%]' progress={progress} />
            </li>
            <li className={PROOF_ITEM}>
              <ProofIcon name='fps' />
              <span className={PROOF_TEXT}>
                <Trans t={t} i18nKey='lp.hero.proofFps' components={{ b: <b />, n: figure }} />
              </span>
              <ProofBar width='w-full' progress={progress} />
            </li>
            <li className={PROOF_ITEM}>
              <ProofIcon name='input' />
              <span className={PROOF_TEXT}>
                <Trans t={t} i18nKey='lp.hero.proofInput' components={{ b: <b /> }} />
              </span>
              <ProofBar width='w-[66%]' progress={progress} />
            </li>
          </ul>

          <div className='mt-6 flex flex-wrap items-center gap-x-4 gap-y-3'>
            <ul className='flex' aria-label={t('lp.hero.avatarsLabel')}>
              {HERO_AVATARS.map((avatar) => (
                <Avatar key={avatar.slug} slug={avatar.slug} name={avatar.name} size={40} className={AVATAR_LG} />
              ))}
            </ul>
            <p className='min-w-0 text-[.875rem] leading-[1.4] text-[#9AA3B5] [&_b]:font-semibold [&_b]:text-white'>
              <Trans t={t} i18nKey='lp.hero.players' components={{ b: <b />, br: <br /> }} />
            </p>
          </div>
        </div>

        <div className='min-w-0'>{formSlot}</div>
      </div>
    </section>
  );
}
